import * as XLSX from 'xlsx';
import { getDateKey } from './dateRange';

const parseCSVRows = (csv) => {
  const workbook = XLSX.read(csv, { type: 'string' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return [];

  return XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
    header: 1,
    raw: false,
    defval: '',
  });
};

const parseReportDate = (value) => {
  const text = String(value ?? '').trim();
  const usDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (usDate) {
    const year = usDate[3].length === 2 ? `20${usDate[3]}` : usDate[3];
    return `${year}-${usDate[1].padStart(2, '0')}-${usDate[2].padStart(2, '0')}`;
  }

  const isoDate = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoDate) {
    return `${isoDate[1]}-${isoDate[2].padStart(2, '0')}-${isoDate[3].padStart(2, '0')}`;
  }

  return null;
};

const addDays = (dateKey, numberOfDays) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + numberOfDays));
  return date.toISOString().slice(0, 10);
};

const formatReportDate = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return `${month}/${day}/${year}`;
};

const normalizeReportRows = (csv, dates) => {
  const rows = parseCSVRows(csv);
  const headers = rows[0] || [];
  const dateColumnIndex = headers.findIndex((header) => String(header).trim().toLowerCase() === 'date');
  const selectedStart = getDateKey(dates?.start);
  const selectedEnd = getDateKey(dates?.end);

  if (dateColumnIndex < 0 || !selectedStart || !selectedEnd) return rows;

  const dataRows = rows.slice(1);
  const firstReportDate = dataRows
    .map((row) => parseReportDate(row[dateColumnIndex]))
    .find(Boolean);
  const shiftDays = firstReportDate && addDays(firstReportDate, 1) === selectedStart ? 1 : 0;
  const normalizedDataRows = dataRows.flatMap((row) => {
    const reportDate = parseReportDate(row[dateColumnIndex]);
    if (!reportDate) return [row];

    const normalizedDate = addDays(reportDate, shiftDays);
    if (normalizedDate < selectedStart || normalizedDate > selectedEnd) return [];

    const normalizedRow = [...row];
    normalizedRow[dateColumnIndex] = formatReportDate(normalizedDate);
    return [normalizedRow];
  });

  return [headers, ...normalizedDataRows];
};

const escapeCSVCell = (value) => {
  const text = String(value ?? '');
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const rowsToCSV = (rows) => rows.map((row) => {
  let lastValueIndex = row.length - 1;
  while (lastValueIndex >= 0 && (row[lastValueIndex] === '' || row[lastValueIndex] == null)) {
    lastValueIndex -= 1;
  }
  return row.slice(0, lastValueIndex + 1).map(escapeCSVCell).join(',');
}).join('\r\n');

export const prepareCSVReport = ({ csv, dates }) => rowsToCSV(normalizeReportRows(csv, dates));

export const combineCSVReports = ({ firstCSV, secondCSV, firstDates, secondDates }) => {
  const firstRows = normalizeReportRows(firstCSV, firstDates);
  const secondRows = normalizeReportRows(secondCSV, secondDates);
  const firstHeaders = firstRows[0] || [];
  const secondHeaders = secondRows[0] || [];
  const headers = firstHeaders.length >= secondHeaders.length ? firstHeaders : secondHeaders;
  const combinedRows = [headers, ...firstRows.slice(1), ...secondRows.slice(1)];

  return rowsToCSV(combinedRows);
};